/**
 * Subscription Monetization Interface - Subscription Status Card
 * 
 * Displays:
 * - Current plan name and badge
 * - Billing cycle date
 * - Quota usage progress bars (leads, sales reps, offers)
 * - Pending downgrade visibility
 * 
 * Goal: Drive upgrades while minimizing billing disputes and churn.
 */
import React from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { 
  Crown, Zap, Building2, Clock, AlertTriangle, Gauge,
  Users, FileText, ChevronRight, CalendarDays, ArrowDownCircle,
  MessageCircle, Sparkles
} from 'lucide-react';
import {
  formatCapacity, getResource, isResolved, quotaPercentage, warningLevel,
} from '../../utils/quotaPresentation';

const SubscriptionStatusCard = ({ 
  subscription,
  entitlement = null,
  onUpgrade,
  onManageSubscription,
  onCancelDowngrade,
}) => {
  const { t } = useTranslation();
  
  if (!subscription) return null;
  
  const packageKey = subscription.package_key || subscription.packageId || 'TRIAL';
  const packageName = subscription.package_name || subscription.packageName || 'Trial';
  const pendingDowngrade = subscription.pending_downgrade || subscription.pendingDowngrade;
  
  // Package icons and colors
  const packageConfig = {
    TRIAL: { icon: Clock, color: 'bg-blue-100 text-blue-700 border-blue-200', gradient: 'from-blue-500 to-blue-600' },
    STARTER: { icon: Zap, color: 'bg-green-100 text-green-700 border-green-200', gradient: 'from-green-500 to-green-600' },
    PRO: { icon: Crown, color: 'bg-purple-100 text-purple-700 border-purple-200', gradient: 'from-purple-500 to-purple-600' },
    ENTERPRISE: { icon: Building2, color: 'bg-amber-100 text-amber-700 border-amber-200', gradient: 'from-amber-500 to-amber-600' },
  };
  
  const config = packageConfig[packageKey] || packageConfig.TRIAL;
  const PackageIcon = config.icon;
  
  // SSOT-ENTITLEMENT-PRESENTATION-01 — every row is resolved from the
  // backend entitlement contract by its CANONICAL key. The previous
  // `limits['salesReps'] || limits['salesReps_per_month'] || 0` alias chain
  // turned an ABSENT field into "zero permitted" and produced the false
  // "Included users 0 / 0 — limit reached" state. No alias guessing, no
  // client-side fallback entitlement.
  const QUOTA_ROWS = [
    { key: 'included_users', icon: Users, label: t('subscription.includedUsers', 'Included users'), testid: 'included-users' },
    { key: 'leads', icon: Sparkles, label: t('subscription.marketplaceLeads', 'Marketplace leads'), testid: 'leads' },
    { key: 'offers', icon: FileText, label: t('subscription.offersMonth', 'Offers / month'), testid: 'offers' },
    { key: 'quote_requests', icon: FileText, label: t('subscription.quoteRequests', 'Quote requests / month'), testid: 'quote-requests' },
    { key: 'active_threads', icon: MessageCircle, label: t('subscription.activeThreads', 'Active threads'), testid: 'active-threads' },
  ].map((row) => {
    const resource = getResource(entitlement, row.key);
    return {
      ...row,
      resource,
      resolved: isResolved(resource),
      percentage: quotaPercentage(resource),
      warning: warningLevel(resource),
    };
  });

  const entitlementUnavailable = !entitlement || !entitlement.resources;

  // Get progress bar color based on usage percentage
  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-amber-500';
    return 'bg-green-500';
  };
  
  // Format billing date
  const formatBillingDate = () => {
    if (packageKey === 'TRIAL') {
      const endDate = subscription.trial_ends_at || subscription.trialEndsAt;
      if (endDate) {
        return `Trial ends ${formatDateLocale(endDate)}`;
      }
      return 'Trial period';
    }
    
    const nextBilling = subscription.next_billing_date || subscription.nextBillingDate;
    if (nextBilling) {
      return `Next billing: ${formatDateLocale(nextBilling)}`;
    }
    return 'Active subscription';
  };
  
  return (
    <Card className={`border-2 ${config.color} overflow-hidden`} data-testid="subscription-status-card">
      {/* Pending Downgrade Warning */}
      {pendingDowngrade && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center justify-between" data-testid="pending-downgrade-banner">
          <div className="flex items-center gap-2">
            <ArrowDownCircle className="h-5 w-5 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                {t('subscription.downgradingTo', 'Downgrading to {{plan}}', { plan: pendingDowngrade.to_package_name || pendingDowngrade.toPackageName })}
              </p>
              <p className="text-xs text-amber-600">
                {t('subscription.effectiveEndOfPeriod', 'Effective at end of current billing period')}
              </p>
            </div>
          </div>
          {onCancelDowngrade && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onCancelDowngrade}
              className="text-amber-700 border-amber-300 hover:bg-amber-100"
              data-testid="cancel-downgrade-btn"
            >
              {t('subscription.cancelDowngrade', 'Keep Current Plan')}
            </Button>
          )}
        </div>
      )}
      
      <CardHeader className={`bg-gradient-to-r ${config.gradient} text-white pb-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <PackageIcon className="h-6 w-6" />
            </div>
            <div>
              <CardTitle className="text-xl font-bold">{packageName}</CardTitle>
              <p className="text-sm opacity-90 flex items-center gap-1">
                <CalendarDays className="h-3 w-3" />
                {formatBillingDate()}
              </p>
            </div>
          </div>
          <Badge className="bg-white/20 text-white border-0 text-xs">
            {subscription.status === 'ACTIVE' 
              ? t('subscription.active', 'Active')
              : subscription.status === 'TRIALING'
                ? t('subscription.trial', 'Trial')
                : subscription.status
            }
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="pt-5 space-y-4">
        {/* PAY-ENTITLEMENT-RECOVERY-01 — a confirmed payment whose entitlement
            projection has not completed must never be presented as fully
            applied. The backend owns this state; the UI only reports it. */}
        {entitlement?.reconciliation?.pending && (
          <div
            className="flex items-start gap-2 p-3 rounded-lg border bg-sky-50 border-sky-200 text-sky-900"
            data-testid="entitlement-reconciliation-pending"
          >
            <Clock className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <p className="text-sm">
              {t('subscription.entitlementPending',
                 'Your payment is confirmed and your plan is active. Applying your new allowances is still finishing — the figures below may be out of date for a few minutes.')}
            </p>
          </div>
        )}

        {/* Usage Progress Bars */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Gauge className="h-4 w-4" />
            {t('subscription.quotaUsage', 'Quota Usage')}
          </h4>

          {entitlementUnavailable ? (
            <p className="text-sm text-gray-500" data-testid="quota-entitlement-unavailable">
              {t('subscription.quotaUnavailable', 'Quota information is temporarily unavailable.')}
            </p>
          ) : QUOTA_ROWS.map((row) => {
            const RowIcon = row.icon;
            return (
              <div className="space-y-1" key={row.key} data-testid={`quota-row-${row.testid}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 inline-flex items-center gap-1.5">
                    <RowIcon className="h-3.5 w-3.5 text-gray-400" />
                    {row.label}
                  </span>
                  <span
                    className={`font-medium ${row.warning === 'reached' ? 'text-red-600' : 'text-gray-900'}`}
                    data-testid={`quota-value-${row.testid}`}
                  >
                    {formatCapacity(row.resource)}
                  </span>
                </div>
                {!row.resolved ? (
                  <p className="text-[11px] text-gray-400" data-testid={`quota-not-configured-${row.testid}`}>
                    {t('subscription.quotaNotConfigured', 'Not configured')}
                  </p>
                ) : row.percentage === null ? (
                  <div className="h-2 bg-gradient-to-r from-green-200 to-green-300 rounded-full" />
                ) : (
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${getProgressColor(row.percentage)}`}
                      style={{ width: `${row.percentage}%` }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* PXC-B.6 — Deterministic 3-state quota warnings per resource.
            HEALTHY < 80% → no warning
            APPROACHING 80–99% → soft amber warning
            REACHED 100%+ → blocking rose warning
            SSOT-ENTITLEMENT-PRESENTATION-01: unlimited (-1) and
            not-configured resources can never raise a warning, and the
            copy is i18n with EN/TR parity (no English template literals). */}
        {(() => {
          const states = QUOTA_ROWS
            .filter((row) => row.warning)
            .map((row) => ({
              level: row.warning,
              testid: row.testid,
              text: row.warning === 'reached'
                ? t('subscription.quotaLimitReached', '{{label}} limit reached.', { label: row.label })
                : t('subscription.quotaApproachingLimit', 'You are approaching your {{label}} limit.', { label: row.label.toLowerCase() }),
            }));
          if (states.length === 0) return null;
          return (
            <div className="space-y-2" data-testid="quota-warnings">
              {states.map((s) => {
                const cls =
                  s.level === 'reached'
                    ? 'bg-rose-50 border-rose-200 text-rose-800'
                    : 'bg-amber-50 border-amber-200 text-amber-800';
                return (
                  <div
                    key={`${s.testid}-${s.level}`}
                    className={`flex items-center gap-2 p-3 rounded-lg border ${cls}`}
                    data-testid={`quota-warning-${s.testid}-${s.level}`}
                  >
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <p className="text-sm">{s.text}</p>
                  </div>
                );
              })}
            </div>
          );
        })()}
        
        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          {packageKey !== 'ENTERPRISE' && onUpgrade && (
            <Button 
              onClick={() => onUpgrade()}
              className={`flex-1 bg-gradient-to-r ${config.gradient} hover:opacity-90`}
              data-testid="upgrade-btn"
            >
              {t('subscription.upgradePlan', 'Upgrade Plan')}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
          {onManageSubscription && (
            <Button 
              variant="outline" 
              onClick={onManageSubscription}
              className="flex-1"
              data-testid="manage-subscription-btn"
            >
              {t('subscription.managePlan', 'Manage Plan')}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SubscriptionStatusCard;
